import * as React from 'react';

import { isEqual } from 'lodash';
import Modal from 'react-modal';

// @ts-ignore
import AdminAutoMessageSftpCredentialsModal from 'admin/components/AdminAutoMessageSftpCredentialsModal';
import ErrorReporter from 'app/components/ErrorReporter';
import Button from 'components/Button';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import {
  ComposableTable,
  composeMultipleCells,
  GenericRowInterface,
  getButtonCellGenerator,
  getTextCellGenerator,
  ColumnGeneratorFunc,
  getTimestampCellGenerator,
} from 'components/ComposableTable';
import { wrapInHoverCell } from 'components/ComposableTable/generators/HoverCell';
import Heading from 'components/Heading';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import Text from 'components/Text';

// @ts-ignore
import AdminAutoMessageSftpCredentialsConfirmDeleteDialog from './AdminAutoMessageSftpCredentialsConfirmDeleteDialog';

import { AdminAutoMessageSftpCredentials_autoMessaging_sftpCredentials } from './queries/__generated__/AdminAutoMessageSftpCredentials';

type AdminAutoMessagesCredentialsType = AdminAutoMessageSftpCredentials_autoMessaging_sftpCredentials &
  GenericRowInterface;

type Props = {
  autoMessageSftpCredentials?: AdminAutoMessagesCredentialsType[];
  viewingOrgUuid: string;
  refetch?: () => void;
  loading?: boolean;
  onCredentialDeletion: (name: string) => void;
};

type State = {
  sftpCredentialsForModal?: any;
  sftpCredentialsModalIsOpen: boolean;
  usernameForDeleteCredentialConfirmationDialog?: string;
  deleteCredentialConfirmationDialogIsOpen: boolean;
};

type RowType = AdminAutoMessagesCredentialsType & { username: string };

export default class AdminAutoMessageSftpCredentialsView extends React.Component<Props, State> {
  state: State = {
    sftpCredentialsForModal: undefined,
    sftpCredentialsModalIsOpen: false,
    deleteCredentialConfirmationDialogIsOpen: false,
    usernameForDeleteCredentialConfirmationDialog: undefined,
  };

  componentDidUpdate(prevProps: Props) {
    // if we update the sftp creds in the apollo store from the modal, we need to
    // send that update back up to the modal, since the modal receives its data
    // from the component state in this component and not directly from apollo
    const { sftpCredentialsModalIsOpen, sftpCredentialsForModal } = this.state;
    const { autoMessageSftpCredentials } = this.props;

    if (
      isEqual(prevProps.autoMessageSftpCredentials, autoMessageSftpCredentials) ||
      !sftpCredentialsForModal ||
      !autoMessageSftpCredentials
    ) {
      return;
    }

    const selectedCreds = autoMessageSftpCredentials.find(
      creds => creds.credentials.username === sftpCredentialsForModal?.username
    );
    const sftpCredentialsNameWasChanged =
      selectedCreds && selectedCreds.displayName !== sftpCredentialsForModal.displayName;
    if (sftpCredentialsModalIsOpen && sftpCredentialsNameWasChanged) {
      this.showSftpCredentialModal(selectedCreds);
    }
  }

  showSftpCredentialModal = (credentials?: any) => {
    this.setState({ sftpCredentialsForModal: credentials }, () =>
      this.setState({ sftpCredentialsModalIsOpen: true })
    );
  };

  hideSftpCredentialModal = () => {
    this.setState({ sftpCredentialsForModal: undefined, sftpCredentialsModalIsOpen: false });
  };

  showConfirmDeleteModal = (username: string) => {
    this.setState({ usernameForDeleteCredentialConfirmationDialog: username }, () =>
      this.setState({ deleteCredentialConfirmationDialogIsOpen: true })
    );
  };

  hideConfirmDeleteModal = () => {
    this.setState({
      sftpCredentialsForModal: undefined,
      deleteCredentialConfirmationDialogIsOpen: false,
    });
  };

  formatDataToRow = (sftpCredentials: AdminAutoMessagesCredentialsType): RowType => ({
    ...sftpCredentials,
    username: sftpCredentials.credentials.username,
  });

  render() {
    const { loading, autoMessageSftpCredentials, viewingOrgUuid } = this.props;
    const {
      sftpCredentialsModalIsOpen,
      sftpCredentialsForModal,
      usernameForDeleteCredentialConfirmationDialog,
    } = this.state;
    if (loading) {
      return <CenteredLoadingIndicator />;
    }

    if (!autoMessageSftpCredentials) {
      return (
        <ErrorReporter error="AdminAutoMessageSftpCredentialsView - tried to render without credentials" />
      );
    }

    const columnGenerators: ColumnGeneratorFunc<RowType>[] = [
      composeMultipleCells<RowType>([
        getTextCellGenerator('displayName', {
          size: 'medium',
          color: 'brand',
          element: 'div',
          lineHeight: 'xlarge',
        }),
        getTimestampCellGenerator(
          'createdAt',
          { size: 'xsmall', color: 'thunder' },
          'Generated on:'
        ),
      ]),
      wrapInHoverCell<RowType>(
        getButtonCellGenerator({
          fieldForAction: 'username',
          onClickAction: data => this.showConfirmDeleteModal(data),
          buttonLabel: 'Delete',
          buttonProps: {
            level: 'secondary',
            color: 'lava',
            'data-test': 'delete-credentials-on-row-button',
          },
        })
      ),
    ];

    return (
      <React.Fragment>
        <Modal isOpen={this.state.deleteCredentialConfirmationDialogIsOpen}>
          {usernameForDeleteCredentialConfirmationDialog ? (
            <AdminAutoMessageSftpCredentialsConfirmDeleteDialog
              onRequestHide={this.hideConfirmDeleteModal}
              username={usernameForDeleteCredentialConfirmationDialog}
              orgUuid={viewingOrgUuid}
            />
          ) : (
            <ErrorReporter error="AdminAutoMessageSftpCredentialsView - tried to render deletion confirmation without username" />
          )}
        </Modal>
        <Modal isOpen={sftpCredentialsModalIsOpen}>
          <AdminAutoMessageSftpCredentialsModal
            orgUuid={viewingOrgUuid}
            onClose={this.hideSftpCredentialModal}
            credentials={sftpCredentialsForModal}
            onCredentialDeletion={() => {
              if (this.props.refetch) {
                this.props.refetch();
              }
            }}
          />
        </Modal>
        <Row>
          <MediaObject center figure="right">
            <Heading flush>Auto messaging SFTP credentials</Heading>
            <Button onClick={() => this.showSftpCredentialModal()}>Generate new credentials</Button>
          </MediaObject>
        </Row>
        <ComposableTable
          columnGenerators={columnGenerators}
          rows={autoMessageSftpCredentials.map(cred => this.formatDataToRow(cred))}
          keyField="displayName"
          actionsColumn
          handleRowClick={credentials => this.showSftpCredentialModal(credentials)}
        />
        {autoMessageSftpCredentials.length === 0 && (
          <Text align="center" element="div" size="small" data-test="sftp-creds-null-state">
            <Row size="small">
              <Text weight="semibold">No credentials have been generated yet</Text>
            </Row>
            <Text color="thunder">
              Once you’ve generated a set of credentials to enter into your FTP client, they’ll
              appear here.
            </Text>
          </Text>
        )}
      </React.Fragment>
    );
  }
}
